import React from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter, Button } from '@repo/ui';

interface ColumnVisibilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  visibleColumns: Record<string, boolean>;
  onToggleColumn: (key: string) => void;
  onToggleAllColumns: (checked: boolean) => void;
}

const ColumnVisibilityModal = React.memo<ColumnVisibilityModalProps>(
  ({ isOpen, onClose, visibleColumns, onToggleColumn, onToggleAllColumns }) => {
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const savedScrollPositionRef = React.useRef<number | null>(null);
    const isUserInteractionRef = React.useRef<boolean>(false);

    // Restore scroll position when visibleColumns changes (only after user interaction)
    React.useLayoutEffect(() => {
      if (
        isUserInteractionRef.current &&
        savedScrollPositionRef.current !== null &&
        scrollContainerRef.current
      ) {
        // Restore scroll position synchronously before browser paint
        const savedPosition = savedScrollPositionRef.current;
        scrollContainerRef.current.scrollTop = savedPosition;
        savedScrollPositionRef.current = null;
        isUserInteractionRef.current = false;

        // Double-check after a microtask to ensure it stuck
        Promise.resolve().then(() => {
          if (
            scrollContainerRef.current &&
            scrollContainerRef.current.scrollTop !== savedPosition
          ) {
            scrollContainerRef.current.scrollTop = savedPosition;
          }
        });
      }
    }, [visibleColumns]);

    const handleColumnToggle = React.useCallback(
      (key: string, event: React.MouseEvent<HTMLButtonElement>) => {
        if (scrollContainerRef.current) {
          // Capture scroll position before state update
          savedScrollPositionRef.current = scrollContainerRef.current.scrollTop;
          isUserInteractionRef.current = true;

          // Blur the button to prevent focus-related scrolling
          event.currentTarget.blur();

          onToggleColumn(key);
        }
      },
      [onToggleColumn]
    );

    const handleToggleAllColumns = React.useCallback(
      (checked: boolean, event: React.MouseEvent<HTMLButtonElement>) => {
        if (scrollContainerRef.current) {
          // Capture scroll position before state update
          savedScrollPositionRef.current = scrollContainerRef.current.scrollTop;
          isUserInteractionRef.current = true;

          // Blur the button to prevent focus-related scrolling
          event.currentTarget.blur();

          onToggleAllColumns(checked);
        }
      },
      [onToggleAllColumns]
    );

    const getVisibleColumnCount = React.useCallback(() => {
      return Object.values(visibleColumns).filter(Boolean).length;
    }, [visibleColumns]);

    const getTotalColumnCount = React.useCallback(() => {
      return Object.keys(visibleColumns).length;
    }, [visibleColumns]);

    const columnGroups = React.useMemo(
      () => [
        {
          title: 'Basic Information',
          columns: [
            { key: 'clientName', label: 'Client Name', required: false },
            { key: 'createdAt', label: 'Created At', required: false },
            { key: 'updatedAt', label: 'Last Updated', required: false },
            { key: 'paymentLink', label: 'Payment Link', required: false },
          ],
        },
        {
          title: 'Operational Bill',
          columns: [
            { key: 'operationalBillTotal', label: 'Total Amount', required: false },
            { key: 'operationalBillPaid', label: 'Paid Amount', required: false },
            { key: 'operationalBillBalance', label: 'Balance', required: false },
            { key: 'operationalBillIssuedAt', label: 'Issued Date', required: false },
            { key: 'operationalBillDueAt', label: 'Due Date', required: false },
          ],
        },
        {
          title: 'Trust Request Bill',
          columns: [
            { key: 'trustRequestBillTotal', label: 'Total Amount', required: false },
            { key: 'trustRequestBillPaid', label: 'Paid Amount', required: false },
            { key: 'trustRequestBillBalance', label: 'Balance', required: false },
            { key: 'trustRequestBillPending', label: 'Pending Amount', required: false },
            { key: 'trustRequestBillIssuedAt', label: 'Issued Date', required: false },
            { key: 'trustRequestBillDueAt', label: 'Due Date', required: false },
            { key: 'trustRequestCalculation', label: 'Calculation', required: false },
          ],
        },
      ],
      []
    );

    return (
      <Dialog open={isOpen} onClose={onClose} size="lg">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-light">
                Edit Columns
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {getVisibleColumnCount()} of {getTotalColumnCount()} columns visible
              </p>
            </div>
          </div>
        </DialogHeader>

        <DialogBody ref={scrollContainerRef} className="space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Select All Toggle */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <Button
              variant={getVisibleColumnCount() === getTotalColumnCount() ? 'primary' : 'outline'}
              size="sm"
              onClick={e =>
                handleToggleAllColumns(getVisibleColumnCount() !== getTotalColumnCount(), e)
              }
            >
              {getVisibleColumnCount() === getTotalColumnCount() ? 'Deselect All' : 'Select All'}
            </Button>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Toggle all columns at once
            </p>
          </div>

          {/* Column Groups */}
          {columnGroups.map(group => (
            <div key={group.title} className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 pb-2">
                {group.title}
              </h4>
              <div className="space-y-2">
                {group.columns.map(({ key, label, required }) => (
                  <div
                    key={key}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <Button
                        variant={visibleColumns[key] ? 'primary' : 'outline'}
                        size="sm"
                        onClick={e => {
                          if (!required) {
                            handleColumnToggle(key, e);
                          }
                        }}
                        disabled={required}
                        className="min-w-[80px]"
                      >
                        {visibleColumns[key] ? 'Hide' : 'Show'}
                      </Button>
                      <span
                        className={`text-sm flex-1 ${required ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}
                      >
                        {label}
                      </span>
                    </div>
                    {required && (
                      <span className="text-xs font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded ml-3">
                        Required
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </DialogBody>

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Changes are saved automatically
            </div>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </DialogFooter>
      </Dialog>
    );
  }
);

ColumnVisibilityModal.displayName = 'ColumnVisibilityModal';

export default ColumnVisibilityModal;
